import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller, useForm } from 'react-hook-form';
import { Form, Link as RouterLink, useActionData, useSubmit } from 'react-router-dom';
import ksim from '../assets/ksim-console2.png';
import EmailField from '../forms/email-field/EmailField';
import AccountLayout from '../layouts/AccountLayout';
import TermsOfUseDialog from '../terms-of-use-dialog/TermsOfServiceDialog';

interface FormFields {
  name: string;
  email: string;
  captcha: string;
  cookies: boolean;
  terms: boolean;
}

const Register = () => {
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const { control, formState, handleSubmit, register, setError, setValue } = useForm<FormFields>({
    defaultValues: {
      cookies: false,
      email: '',
      name: '',
      terms: false,
      captcha: '',
    },
  });
  const submit = useSubmit();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isLoading, setLoading] = useState(false);

  // POST form to route
  const onSubmit = ({ captcha, email, name }) => {
    setLoading(true);
    submit({ captcha, email, name }, { method: 'post' });
  };

  const actionData = useActionData() as { errors: Record<keyof FormFields, string> };
  useEffect(() => {
    if (actionData?.errors) {
      setLoading(false);
      for (const fieldName in actionData.errors) {
        setError(fieldName.toLowerCase() as keyof FormFields, { message: actionData.errors[fieldName] });
      }
    }
  }, [actionData]);

  const handleReCaptchaVerify = useCallback(async () => {
    if (executeRecaptcha) {
      const token = await executeRecaptcha('registerAccount');
      setValue('captcha', token);
    }
    handleSubmit(onSubmit)();
  }, [executeRecaptcha]);

  useEffect(() => {
    register('captcha', { required: 'We cannot generate a ReCaptcha token that the server needs to verify' });
  });

  return (
    <AccountLayout>
      <Box mt={{ xs: 4, sm: 14 }} mb={4.5}>
        <Box component="img" alt="K-Sim" height={192} src={ksim} />
      </Box>
      <Typography variant="h5" mb={2}>
        Create an account
      </Typography>

      <Box
        component={Form}
        noValidate
        onSubmit={handleReCaptchaVerify}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Please fill in your full name.' }}
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              error={!!formState.errors?.name}
              helperText={formState.errors?.name?.message as string}
              InputLabelProps={{ shrink: true }}
              label="Name"
              placeholder="Enter your full name"
              required
              type="email"
            />
          )}
        />

        <EmailField control={control} />

        <Controller
          name="terms"
          control={control}
          rules={{ required: 'Please accept the terms of use.' }}
          render={({ field }: any) => (
            <FormControl required error={!!formState.errors?.terms}>
              <FormControlLabel
                {...field}
                control={<Checkbox required />}
                label={
                  <>
                    I agree to Kongsberg{' '}
                    <Link
                      onClick={e => {
                        e.preventDefault();
                        setShowTermsOfUse(true);
                      }}
                    >
                      terms of use
                    </Link>{' '}
                    for using K-Sim Connect
                  </>
                }
                sx={{ mb: -2, textAlign: 'left' }}
              />
              {formState.errors?.terms && (
                <FormHelperText sx={{ marginTop: '16px', marginLeft: '30px' }}>
                  {(formState.errors?.terms?.message as string) ?? undefined}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="cookies"
          control={control}
          rules={{ required: 'You have to allow cookies to continue.' }}
          render={({ field }: any) => (
            <FormControl required error={!!formState.errors?.cookies}>
              <FormControlLabel
                {...field}
                control={<Checkbox required />}
                label={<>I allow this website to use cookies</>}
                sx={{ textAlign: 'left' }}
              />
              {formState.errors?.cookies && (
                <FormHelperText sx={{ marginLeft: '30px' }}>
                  {(formState.errors?.cookies?.message as string) ?? undefined}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        {formState.errors?.captcha && (
          <Alert severity="error" sx={{ textAlign: 'left' }}>
            <AlertTitle sx={{ mb: '4px !important' }}>Could not submit form</AlertTitle>
            {formState.errors?.captcha.message}
          </Alert>
        )}

        <LoadingButton loading={isLoading} variant="contained" type="submit" sx={{ alignSelf: 'center' }}>
          Create account
        </LoadingButton>
        <Typography>
          I already have an account. I just want to{' '}
          <Link component={RouterLink} to="/login">
            sign in
          </Link>
          .
        </Typography>

        <Typography color="text.secondary" variant="caption" mt={6}>
          This site is protected by reCAPTCHA and the Google{' '}
          <Link href="https://policies.google.com/privacy" rel="noreferrer" target="_blank">
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link href="https://policies.google.com/terms" rel="noreferrer" target="_blank">
            Terms of Service
          </Link>{' '}
          apply.
        </Typography>
      </Box>

      <TermsOfUseDialog open={showTermsOfUse} onClose={() => setShowTermsOfUse(false)} />
    </AccountLayout>
  );
};

export default Register;
